import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { PAYMENT_MODES, TAC_RATE } from '../utils/constants';
import { useCruiseSettings, getCruiseDecks } from '../hooks/useCruiseSettings';
import { usePricingRates } from '../hooks/usePricing';
import { priceLoadFailedMessage, PRICE_LOADING_MESSAGE } from '../utils/pricing';
import { generateBookingReference } from '../utils/bookingReference';
import { getWholeCruiseClosure, closureMessage } from '../utils/closures';

const toInputDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return '';
  const [d, m, y] = ddmmyyyy.split('-');
  return `${y}-${m}-${d}`;
};
const toDisplayDate = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-');
  return `${d}-${m}-${y}`;
};

export default function ReviewDraft({ draftData, onNavigate }) {
  const { settings: cruiseSettings } = useCruiseSettings();

  const [inputDate, setInputDate]         = useState(toInputDate(draftData?.date || ''));
  const [cruise, setCruise]               = useState(draftData?.cruise_type || '');
  const [deck, setDeck]                   = useState(draftData?.deck || '');
  const [bookingName, setBookingName]     = useState(draftData?.booking_name || '');
  const [partnerName, setPartnerName]     = useState(draftData?.partner_name || '');
  const [phone, setPhone]                 = useState(draftData?.phone_number || '');
  const [adults, setAdults]               = useState(draftData?.adults ?? '');
  const [children, setChildren]           = useState(draftData?.children ?? '');
  // Food preferences (Dinner Cruise buffet only). Hidden + saved as 0 otherwise.
  const [foodNonVeg, setFoodNonVeg]       = useState(draftData?.food_non_veg ?? 0);
  const [foodVeg, setFoodVeg]             = useState(draftData?.food_veg ?? 0);
  const [foodJain, setFoodJain]           = useState(draftData?.food_jain ?? 0);
  const [totalAmount, setTotalAmount]     = useState(draftData?.total_amount ?? '');
  const [tac, setTac]                     = useState(draftData?.tac ?? '');
  const [tacManual, setTacManual]         = useState(false);
  const [paymentMode, setPaymentMode]     = useState(draftData?.payment_mode || '');
  const [paymentStatus, setPaymentStatus] = useState(draftData?.payment_status || 'Pending');
  const [notes, setNotes]                 = useState(draftData?.notes || '');
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');

  const currentCruiseSetting = cruiseSettings.find(s => s.cruise_name === cruise);
  const availableDecks = currentCruiseSetting ? getCruiseDecks(currentCruiseSetting) : [];
  const activeCruiseNames = cruiseSettings.length > 0
    ? cruiseSettings.map(s => s.cruise_name)
    : ['Sunset Cruise', 'Sundown Cruise', 'Dinner Cruise'];

  // Live per-head rates for the chosen cruise + deck from the `pricing` table
  // (hooks/usePricing.js). Never a hardcoded constant: that drifted silently
  // whenever a price was edited in the Pricing screen. ratesStatus is 'idle'
  // until both cruise and deck are chosen, then loading / ok / error.
  const { rates, status: ratesStatus, retry: retryRates } = usePricingRates(cruise, deck);

  // Recalculate total when pax or the live rates change. With no rates
  // (loading or failed) the total is left alone — never priced from a
  // constant and never from zero; handleSubmit refuses instead.
  useEffect(() => {
    if (!rates) return;
    const auto = (Number(adults) || 0) * rates.adult + (Number(children) || 0) * rates.child;
    if (auto > 0) setTotalAmount(auto);
  }, [rates, adults, children]);

  // Recalculate TAC unless manually overridden
  useEffect(() => {
    if (!tacManual) {
      const total = Number(totalAmount) || 0;
      setTac(partnerName.trim() ? Math.round(total * TAC_RATE) : 0);
    }
  }, [partnerName, totalAmount, tacManual]);

  useEffect(() => {
    if (!partnerName.trim()) setTacManual(false);
  }, [partnerName]);

  const netTotal = (Number(totalAmount) || 0) - (Number(tac) || 0);

  // Food preferences apply ONLY to the Dinner Cruise; must total adults + children.
  const isDinnerCruise = (cruise || '').toLowerCase().includes('dinner');
  const foodRequired = (Number(adults) || 0) + (Number(children) || 0);
  const foodTotal = (Number(foodNonVeg) || 0) + (Number(foodVeg) || 0) + (Number(foodJain) || 0);
  const foodValid = !isDinnerCruise || foodTotal === foodRequired;

  const handleCruiseChange = (val) => {
    setCruise(val);
    const setting = cruiseSettings.find(s => s.cruise_name === val);
    const decks = setting ? getCruiseDecks(setting) : [];
    setDeck(decks.length === 1 ? decks[0] : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!inputDate || !cruise || !deck || !bookingName || !adults) {
      setError('Please fill all required fields.');
      return;
    }
    // Hard stop: no live price, no booking. This screen has no FOC option, so
    // there is no exemption — a manually typed total does not bypass it either.
    if (ratesStatus !== 'ok') {
      setError(ratesStatus === 'loading'
        ? PRICE_LOADING_MESSAGE
        : priceLoadFailedMessage(cruise, deck, 'The booking'));
      return;
    }
    // Dinner Cruise: food preferences must total adults + children.
    if (isDinnerCruise && foodTotal !== foodRequired) {
      setError(`Food preferences must total ${foodRequired} (adults + children).`);
      return;
    }
    setSaving(true);

    const displayDate = toDisplayDate(inputDate);
    const payload = {
      date: displayDate,
      cruise_type: cruise,
      deck,
      booking_name: bookingName.trim(),
      partner_name: partnerName.trim() || null,
      guest_phone: phone.trim() || null,
      adults: Number(adults),
      children: Number(children) || 0,
      food_non_veg: isDinnerCruise ? (Number(foodNonVeg) || 0) : 0,
      food_veg:     isDinnerCruise ? (Number(foodVeg) || 0) : 0,
      food_jain:    isDinnerCruise ? (Number(foodJain) || 0) : 0,
      total_amount: Number(totalAmount) || 0,
      tac: Number(tac) || 0,
      net_total: netTotal,
      payment_mode: paymentMode || null,
      payment_status: paymentStatus || null,
      notes: notes.trim() || null,
    };

    // Block confirming a draft into a booking when the whole cruise is closed
    // online for this date (deck-only closures don't block here).
    const closure = await getWholeCruiseClosure(cruise, displayDate);
    if (closure) { setSaving(false); setError(closureMessage(closure, displayDate)); return; }

    // Insert into bookings and get the ID back
    const insertRes = await supabase.from('bookings').insert(payload).select('id').single();
    if (insertRes.error) {
      setSaving(false);
      setError(insertRes.error.message);
      return;
    }

    // Generate booking reference
    const bookingId = insertRes.data.id;
    const cruiseCode = currentCruiseSetting?.cruise_code;
    if (cruiseCode) {
      generateBookingReference(bookingId, cruiseCode, displayDate).catch(() => {});
    }

    // Mark draft as confirmed
    const updateRes = await supabase
      .from('draft_inquiries')
      .update({ status: 'confirmed' })
      .eq('id', draftData.id);

    setSaving(false);
    if (updateRes.error) {
      setError(updateRes.error.message);
      return;
    }

    setSuccess(`Booking confirmed and added to ${displayDate}`);
    setTimeout(() => onNavigate('draft-inquiries'), 1800);
  };

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('draft-inquiries')}>← Back</button>
        <div>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#0A1628' }}>
            Review &amp; Confirm
          </h2>
          <p style={{ fontSize: '0.73rem', color: 'var(--color-ink-muted)', marginTop: 1 }}>
            Correct any fields, then confirm to add to bookings
          </p>
        </div>
      </div>

      <div className="form-card">
        {ratesStatus === 'error' && (
          <div className="toast toast-error" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1 }}>Price couldn't be loaded for {cruise} · {deck}. This booking can't be confirmed until pricing loads.</span>
            <button type="button" className="btn btn-outline btn-sm" onClick={retryRates} style={{ flexShrink: 0 }}>Retry</button>
          </div>
        )}
        {error   && <div className="toast toast-error">{error}</div>}
        {success && <div className="toast toast-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-grid">

            <div className="form-group">
              <label>Date <span className="required">*</span></label>
              <input
                type="date" value={inputDate}
                onChange={(e) => setInputDate(e.target.value)}
                style={{ colorScheme: 'light' }} required
              />
            </div>

            <div className="form-group">
              <label>Cruise Type <span className="required">*</span></label>
              <select value={cruise} onChange={(e) => handleCruiseChange(e.target.value)} required>
                <option value="">Select cruise…</option>
                {activeCruiseNames.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Deck <span className="required">*</span></label>
              <select value={deck} onChange={(e) => setDeck(e.target.value)} required disabled={!cruise}>
                <option value="">Select deck…</option>
                {availableDecks.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Booking Name <span className="required">*</span></label>
              <input type="text" value={bookingName}
                onChange={(e) => setBookingName(e.target.value)}
                placeholder="Guest / company name" required />
            </div>

            <div className="form-group">
              <label>Partner Name</label>
              <input type="text" value={partnerName}
                onChange={(e) => setPartnerName(e.target.value)}
                placeholder="Travel agent (optional)" />
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input type="text" value={phone}
                onChange={(e) => setPhone(e.target.value)} placeholder="+91…" />
            </div>

            <div className="form-group">
              <label>Adults <span className="required">*</span></label>
              <input type="number" min="1" value={adults}
                onChange={(e) => setAdults(e.target.value)} placeholder="0" required />
            </div>

            <div className="form-group">
              <label>Children</label>
              <input type="number" min="0" value={children}
                onChange={(e) => setChildren(e.target.value)} placeholder="0" />
            </div>

            {/* Food preferences — Dinner Cruise buffet only */}
            {isDinnerCruise && (
              <div className="form-group full-width">
                <label>Food preferences (buffet) <span className="required">*</span></label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', fontWeight: 600 }}>Non-veg</label>
                    <input type="number" min="0" value={foodNonVeg} onChange={(e) => setFoodNonVeg(e.target.value)} placeholder="0" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', fontWeight: 600 }}>Veg</label>
                    <input type="number" min="0" value={foodVeg} onChange={(e) => setFoodVeg(e.target.value)} placeholder="0" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', fontWeight: 600 }}>Jain</label>
                    <input type="number" min="0" value={foodJain} onChange={(e) => setFoodJain(e.target.value)} placeholder="0" />
                  </div>
                </div>
                <div style={{ marginTop: 6, fontSize: '0.78rem', fontWeight: 600, color: foodValid ? 'var(--color-ink-muted)' : '#dc2626' }}>
                  {foodValid
                    ? `Total ${foodTotal} of ${foodRequired} (adults + children).`
                    : `Food preferences must total ${foodRequired} (adults + children). Currently ${foodTotal}.`}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Total Amount (₹)</label>
              <input type="number" min="0" value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)} placeholder="Auto-calculated" />
              {ratesStatus === 'ok' && (
                <span className="calc-note">
                  {adults || 0}×₹{rates.adult} + {children || 0}×₹{rates.child}
                </span>
              )}
              {ratesStatus === 'loading' && <span className="calc-note">Loading price…</span>}
            </div>

            <div className="form-group">
              <label>TAC (₹)</label>
              <input type="number" min="0" value={tac}
                onChange={(e) => { setTac(e.target.value); setTacManual(true); }}
                placeholder="0" />
              <span className="calc-note">10% of Total when Partner Name set</span>
            </div>

            <div className="form-group">
              <label>Net Total (₹)</label>
              <input type="number" value={isNaN(netTotal) ? '' : netTotal}
                readOnly style={{ color: '#7a5a10', fontWeight: 700 }} />
              <span className="calc-note">Total − TAC (not editable)</span>
            </div>

            <div className="form-group">
              <label>Payment Mode</label>
              <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                <option value="">Select…</option>
                {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Payment Status</label>
              <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                <option value="">Select…</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes…" />
            </div>
          </div>

          <div style={{ marginTop: 24, display: 'flex', gap: 10 }}>
            <button type="submit" className="btn btn-gold" disabled={saving || ratesStatus === 'loading' || ratesStatus === 'error'}>
              {saving ? 'Confirming…' : '✓ Confirm Booking'}
            </button>
            <button type="button" className="btn btn-outline"
              onClick={() => onNavigate('draft-inquiries')}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
